import { makeStyles, Text, tokens } from "@fluentui/react-components";
import React from "react";

import { ExternalLink } from "./ExternalLink";

type LinkItem = {
  id: string;
  category: string;
  name: string;
  href: string;
};

type Props = {
  links?: LinkItem[];
};

const createLinkItem = (
  id: string,
  name: string,
  href: string,
  category: string = "",
): LinkItem => ({
  id,
  category,
  name,
  href,
});

const defaultLinks: LinkItem[] = [
  createLinkItem(
    "1",
    "人事FAQチャットボット",
    "https://chat4-37.chatdealer.jp/chat/url/18kQBfq?vgAtxt=tCSO3Chat4-37",
  ),
  createLinkItem(
    "2",
    "外為法報告チャットボット",
    "https://chat1-3b4.chatdealer.jp/chat/url/1ZxpMs4?vgAtxt=6gGEBChat1-3b4",
  ),
  createLinkItem(
    "3",
    "SFMトレジャリーサービス部FAQチャットボット",
    "https://chat1-3b4.chatdealer.jp/chat/url/2cfIaGf?vgAtxt=6gGEBChat1-3b4",
  ),
  createLinkItem(
    "4",
    "支払全般chatbot",
    "https://teams.microsoft.com/l/app/1da11a76-f180-4117-b9e0-06d39883a746?source=app-details-dialog",
  ),
  createLinkItem(
    "5",
    "消費税chatbot",
    "https://teams.microsoft.com/l/app/503eff9a-2cd6-482a-9991-260e29e558a5?source=app-details-dialog",
  ),
  createLinkItem(
    "6",
    "金属コンシェルジュ(金属システム全般、Σ営業システム(金属))",
    "https://teams.microsoft.com/l/chat/19:ec31e0a5-3fbd-426f-a56e-dca603bf2231_3d3a5acb-4191-4af3-ad0f-41cfa9a39e86@unq.gbl.spaces/conversations?context=%7B%22contextType%22%3A%22chat%22%7D",
  ),
  createLinkItem(
    "7",
    "NormNavi（社則細則検索チャットボット）",
    "https://normnavi.sumitomocorp.co.jp/",
  ),
];

export const ExternalLinkArea: React.FC<Props> = (props) => {
  const { links = defaultLinks } = props;
  const styles = useStyles();

  const groupedLinks = links.reduce<Record<string, LinkItem[]>>((acc, link) => {
    acc[link.category] ??= [];
    acc[link.category].push(link);
    return acc;
  }, {});

  const categories = Object.keys(groupedLinks);

  return (
    <div className={styles.root}>
      <Text as="h2" className={styles.title}>
        ほかのFAQを使用したい方はこちら
      </Text>
      <div className={styles.flexContainer}>
        {categories.map((category) => (
          <div key={category} className={styles.column}>
            <Text className={styles.categoryTitle}>{category}</Text>
            <div className={styles.linkList}>
              {groupedLinks[category].map((link) => (
                <div key={link.id} className={styles.linkItem}>
                  <ExternalLink text={link.name} href={link.href} target="_blank" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
    height: "100%", // 親コンテナに合わせて高さを100%に設定
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalS,
  },
  flexContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    flex: 1,
    borderTop: `1px dashed ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalM,
  },
  column: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: "calc(33.33% - 16px)",
    position: "relative",
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  categoryTitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1, // 元の色に戻す
    marginBottom: tokens.spacingVerticalXS,
  },
  linkList: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: tokens.spacingVerticalS,
    width: "100%",
  },
  linkItem: {
    display: "flex",
    alignItems: "center",
    flex: "1 1 0", /* grow:1, shrink:1, basis:0 */
    minWidth: "50%", /* 親幅の半分以下には縮まない */
    boxSizing: "border-box",
  },
  divider: {
    position: "absolute",
    left: "50%",
    top: 0,
    bottom: 0,
    width: "1px",
    borderRight: `1px dashed ${tokens.colorNeutralStroke1}`,
    height: "100%",
    transform: "translateX(-50%)",
  },
});
